import { ReactNode, useEffect } from 'react';
import { AppBar, Toolbar, Typography, Container, Box, Button, useTheme, useMediaQuery, Alert } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import WarningIcon from '@mui/icons-material/Warning';
import { NavBar } from './NavBar';
import { useAuth } from '../contexts/AuthContext';
import { Environment } from '../vite-env';

interface PageHeaderProps {
    children: ReactNode;
    currentPath: string;
    rightContent?: ReactNode;
}

export function PageHeader({ children, currentPath, rightContent }: PageHeaderProps) {
    const { logout, role } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const environment = import.meta.env.VITE_ENVIRONMENT as Environment;
    const isLive = environment === 'live';
    const isDev = environment === 'dev';

    // Update document title based on role
    useEffect(() => {
        document.title = role === 'teacher' ? 'GCWP Invoices' : 'GCWP Monitor';
    }, [role]);

    const handleLogout = () => {
        logout();
        window.history.pushState({}, '', '/');
        window.dispatchEvent(new PopStateEvent('popstate'));
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            {/* Environment Warning Banner */}
            {!isLive && (
                <Alert
                    severity="warning"
                    icon={<WarningIcon />}
                    sx={{
                        borderRadius: 0,
                        '& .MuiAlert-message': {
                            width: '100%',
                            textAlign: 'center',
                            fontWeight: 'medium',
                            paddingRight: '45px',
                        },
                    }}
                >
                    Environment: {environment.toUpperCase()}
                    {isDev ? '' : `, Role: ${role || 'undefined'}`}
                </Alert>
            )}

            <AppBar position="static" sx={{ backgroundColor: 'background.paper' }}>
                <Toolbar>
                    {isMobile && (
                        <>
                            <NavBar currentPath={currentPath} onLogout={isMobile ? handleLogout : undefined} />
                            <Box sx={{ flexGrow: 1 }} />
                            {rightContent}
                            <AppTitle />
                        </>
                    )}
                    {!isMobile && (
                        <>
                            <AppTitle />
                            <NavBar currentPath={currentPath} onLogout={isMobile ? handleLogout : undefined} />
                            <Box sx={{ flexGrow: 1 }} />
                            {rightContent}
                            <Button
                                color="inherit"
                                startIcon={<LogoutIcon />}
                                onClick={handleLogout}
                                sx={{ color: 'text.secondary' }}
                            >
                                Logout
                            </Button>
                        </>
                    )}
                </Toolbar>
            </AppBar>

            <Container maxWidth="xl" sx={{ py: 4, flexGrow: 1 }}>
                {children}
            </Container>
        </Box>
    );
}

function AppTitle() {
    const { role } = useAuth();
    const handleHomeClick = () => {
        window.location.href = '/';
    };

    const title = role === 'teacher' ? 'GCWP Invoices' : 'GCWP Monitor';

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={handleHomeClick}>
            <Typography variant="h6" component="h1" sx={{ mr: { xs: 0, md: 4 }, color: 'text.primary' }}>
                {title}
            </Typography>
        </Box>
    );
}
